import React from 'react';

const DIMENSION_MAPPING = [
  {
    current: 'Technical Skills',
    onet: 'Skills (technical/process)',
    shrm: 'Functional capability and job execution evidence',
  },
  {
    current: 'Experience & Scope',
    onet: 'Worker experience and occupational requirements',
    shrm: 'Role level, responsibility, and delivery complexity',
  },
  {
    current: 'Occupational Knowledge',
    onet: 'Knowledge domains and context familiarity',
    shrm: 'Business/industry understanding and contextual fluency',
  },
  {
    current: 'Cognitive Abilities',
    onet: 'Abilities (reasoning and analysis)',
    shrm: 'Judgment quality and solution architecture rigor',
  },
  {
    current: 'Tools & Technology',
    onet: 'Tools and technology requirements',
    shrm: 'Platform readiness and environment fit',
  },
  {
    current: 'Transferable Competencies',
    onet: 'Cross-occupational skills and generalized work activities',
    shrm: 'Adaptability and cross-functional mobility signals',
  },
  {
    current: 'Credentials & Learning',
    onet: 'Education, credentials, and preparation pathways',
    shrm: 'Qualification evidence and continuous development',
  },
  {
    current: 'Work Style, Collaboration & Leadership',
    onet: 'Work styles and interpersonal requirements',
    shrm: 'Behavioral competency and team leadership indicators',
  },
];

export const DimensionStandardsPage: React.FC = () => {
  return (
    <div className="v2-page" style={{ maxWidth: 1080 }}>
      <div className="v2-page-header">
        <h1>Dimension Standards Mapping</h1>
        <p>
          xRank dimension taxonomy is mapped to O*NET content model constructs and SHRM competency guidance.
          This page documents naming, mapping intent, and interpretation guidance.
        </p>
      </div>

      <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="v2-card-header">
          <h2>Methodology Summary</h2>
        </div>
        <div className="v2-card-body" style={{ display: 'grid', gap: 'var(--space-3)', color: 'var(--text-secondary)' }}>
          <p style={{ margin: 0 }}>
            The eight dimensions are not arbitrary labels. They represent a practical, product-focused taxonomy
            aligned to established talent frameworks.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>O*NET alignment: worker attributes, occupational requirements, tools/technology, and context.</li>
            <li>SHRM alignment: functional, behavioral, and transferable competency assessment.</li>
            <li>Selection science alignment: multi-domain evidence outperforms single-signal screening.</li>
          </ul>
          <p style={{ margin: 0 }}>
            This mapping improves interpretability and comparability while preserving existing model keys and score semantics.
          </p>
        </div>
      </div>

      <div className="v2-card">
        <div className="v2-card-header">
          <h2>Old to New Taxonomy Mapping</h2>
        </div>
        <div className="v2-card-body" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 940, fontSize: 'var(--font-size-sm)' }}>
            <thead>
              <tr>
                <th style={thStyle}>Dimension Name</th>
                <th style={thStyle}>O*NET Mapping</th>
                <th style={thStyle}>SHRM/Selection Mapping</th>
              </tr>
            </thead>
            <tbody>
              {DIMENSION_MAPPING.map((row) => (
                <tr key={row.current}>
                  <td style={tdStrongStyle}>{row.current}</td>
                  <td style={tdStyle}>{row.onet}</td>
                  <td style={tdStyle}>{row.shrm}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-muted)',
  fontSize: 'var(--font-size-xs)',
  letterSpacing: '0.02em',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-secondary)',
  verticalAlign: 'top',
};

const tdStrongStyle: React.CSSProperties = {
  ...tdStyle,
  color: 'var(--text-primary)',
  fontWeight: 600,
};

